/**
 * Permission definitions for different resources.
 *
 * ## Naming Convention
 * Pattern: `namespace:action[:qualifier]`
 *
 * ## Actions
 * - `create` - Create new records
 * - `read` - View/list records
 * - `update` - Modify existing records
 * - `delete` - Remove records
 * - `access` - Access a tool or feature dashboard
 * - `use` - Execute/use a tool or feature
 *
 * ## Qualifiers
 * - `:own_handling_parties` - User is in the handling party assignment for the record
 * - `:is_owner` - User is set as the owner of the record
 * - `:owner_without_handling_parties` - User is owner AND no handling parties are assigned
 * - `:own_handling_parties_or_is_owner` - User is either in handling parties OR is the owner
 * - `:created_by_user` - User created the record (used for non-ownership scenarios)
 * - `:assigned_user` - User is specifically assigned to the record
 * - `:unallocated` - Record has no handling parties assigned
 *
 * ## Admin Permission Hierarchy
 * - `super:admin` - System-wide access across ALL organisations. Used for platform operators.
 * - `org:admin` - Full admin access within a SINGLE organisation. Cannot access other orgs.
 * - `system:admin` - Reserved for system-level operations (e.g., background jobs, migrations).
 *
 * ## Special Permission Behaviors
 * **Claim permissions with `:own_handling_parties` qualifier:**
 * These permissions also grant access to unallocated claims that the user created.
 * This allows users to access claims they initiated before the claim is assigned to
 * handling parties. See `canReadClaim` and `canUpdateClaim` in claim.ts for enforcement.
 *
 * ## Intentionally Missing DELETEs
 * The following resources intentionally lack DELETE permissions
 * as they require archival for compliance/audit purposes:
 * - claim, complaint, incident, obligation
 * - msp, conflict_of_interest, user_attestation
 * - pds, pds_version (versioned data)
 */
export const PERMISSIONS = {
    // Super admin
    SUPER_ADMIN: 'super:admin',

    // Organization Administration
    ORG_ADMIN: 'org:admin',

    // User management
    USER_CREATE: 'user:create',
    USER_READ: 'user:read',
    USER_UPDATE: 'user:update',

    // Task management
    TASK_MANAGE: 'task:manage',
    TASK_REASSIGN_SELECTED: 'task:reassign:selected',

    // Claims management
    CLAIM_CREATE: 'claim:create',
    CLAIM_UPDATE: 'claim:update',
    CLAIM_READ: 'claim:read',
    CLAIM_LIST: 'claim:list',
    CLAIM_READ_UNALLOCATED: 'claim:read:unallocated',
    CLAIM_UPDATE_UNALLOCATED: 'claim:update:unallocated',
    CLAIM_READ_OWN_HANDLING_PARTIES: 'claim:read:own_handling_parties',
    CLAIM_UPDATE_OWN_HANDLING_PARTIES: 'claim:update:own_handling_parties',
    CLAIM_UPDATE_IS_OWNER: 'claim:update:is_owner',
    /**
     * CUR-2675: every claim in the organisation, read only (the Accounts role). Unlike `claim:read`,
     * which only organisation administrators hold and which several places read as "can see
     * everything", nothing but the claim read scope reads this one.
     */
    CLAIM_READ_ALL: 'claim:read:all',
    /** CUR-2675: the final approval on payments and marking them paid (PAY-A5, A9), held by the Accounts role only. */
    CLAIM_PAYMENT_ACCOUNTS: 'claim_payment:accounts',

    // Claim Decision Engine
    /** Manage decision engine configuration (loss causes, red flags, limits, etc.) */
    CLAIM_DECISION_ENGINE_MANAGE: 'claim_decision_engine:manage',
    /** Use decision engine features (run assessments, view results) */
    CLAIM_DECISION_ENGINE_USE: 'claim_decision_engine:use',

    // Contact management
    CONTACT_READ: 'contact:read',
    CONTACT_CREATE: 'contact:create',
    CONTACT_UPDATE: 'contact:update',
    CONTACT_DELETE: 'contact:delete',

    // Complaint management
    COMPLAINT_CREATE: 'complaint:create',
    COMPLAINT_READ: 'complaint:read',
    COMPLAINT_LIST: 'complaint:list',
    COMPLAINT_READ_OWN_HANDLING_PARTIES_OR_IS_OWNER: 'complaint:read:own_handling_parties_or_is_owner',
    COMPLAINT_UPDATE: 'complaint:update',
    COMPLAINT_UPDATE_OWN_HANDLING_PARTIES: 'complaint:update:own_handling_parties',
    COMPLAINT_UPDATE_IS_OWNER: 'complaint:update:is_owner',

    // Incident management
    INCIDENT_CREATE: 'incident:create',
    INCIDENT_READ: 'incident:read',
    INCIDENT_READ_OWN_HANDLING_PARTIES: 'incident:read:own_handling_parties', // Read incidents where the incident is assigned to the user's handling parties
    INCIDENT_READ_OWNER_WITHOUT_HANDLING_PARTIES: 'incident:read:owner_without_handling_parties',
    INCIDENT_UPDATE: 'incident:update',
    INCIDENT_UPDATE_IS_OWNER: 'incident:update:is_owner',
    INCIDENT_UPDATE_OWN_HANDLING_PARTIES: 'incident:update:own_handling_parties',

    // Obligation management
    OBLIGATION_READ: 'obligation:read',
    OBLIGATION_READ_OWN_HANDLING_PARTIES: 'obligation:read:own_handling_parties',
    OBLIGATION_UPDATE: 'obligation:update',
    OBLIGATION_UPDATE_OWN_HANDLING_PARTIES: 'obligation:update:own_handling_parties',
    OBLIGATION_UPDATE_IS_OWNER: 'obligation:update:is_owner',

    // Message classification (auto-detect tool). Access is deliberately
    // all-or-nothing: the `auto_detect_user` role holds the full family and is
    // the only role with any of these grants — org admins need that role too.
    // `:access` gates the tool surface (nav + every tool route); the CRUD
    // permissions govern the entity within it.
    MESSAGE_CLASSIFICATION_ACCESS: 'message_classification:access',
    MESSAGE_CLASSIFICATION_CREATE: 'message_classification:create',
    MESSAGE_CLASSIFICATION_READ: 'message_classification:read',
    MESSAGE_CLASSIFICATION_UPDATE: 'message_classification:update',
    MESSAGE_CLASSIFICATION_DELETE: 'message_classification:delete',

    // Message-classification vulnerability detection (D6 — separate permission
    // scope from base access because vulnerability data carries higher
    // legal/ethical-care duty, especially for sensitive types like family violence)
    MESSAGE_CLASSIFICATION_VULNERABILITY_VIEW: 'message_classification:vulnerability:view',
    MESSAGE_CLASSIFICATION_VULNERABILITY_MANAGE: 'message_classification:vulnerability:manage',
    MESSAGE_CLASSIFICATION_VULNERABILITY_VIEW_SENSITIVE: 'message_classification:vulnerability:view_sensitive',

    // Product type
    PRODUCT_TYPE_CREATE: 'product_type:create',
    PRODUCT_TYPE_UPDATE: 'product_type:update',
    PRODUCT_TYPE_DELETE: 'product_type:delete',

    // Supplier
    SUPPLIER_READ: 'supplier:read',
    SUPPLIER_CREATE: 'supplier:create',
    SUPPLIER_UPDATE: 'supplier:update',
    SUPPLIER_DELETE: 'supplier:delete',

    // Financial section
    FINANCIAL_SECTION_READ: 'financial_section:read',
    FINANCIAL_SECTION_UPDATE: 'financial_section:update',

    // Transaction type
    TRANSACTION_TYPE_READ: 'transaction_type:read',

    // Cost code
    COST_CODE_READ: 'cost_code:read',

    /** PDS = Product Disclosure Statement - regulatory documents that outline product terms */
    PDS_READ: 'pds:read',
    PDS_CREATE: 'pds:create',
    PDS_UPDATE: 'pds:update',

    // PDS version
    PDS_VERSION_READ: 'pds_version:read',
    PDS_VERSION_CREATE: 'pds_version:create',
    PDS_VERSION_UPDATE: 'pds_version:update',

    // Insurer
    INSURER_READ: 'insurer:read',
    INSURER_CREATE: 'insurer:create',
    INSURER_UPDATE: 'insurer:update',
    INSURER_DELETE: 'insurer:delete',

    // Billing
    BILLING_READ: 'billing:read',
    BILLING_ADD_FUNDS: 'billing:add_funds',

    // Indicator definition
    INDICATOR_DEFINITION_READ: 'indicator_definition:read',
    INDICATOR_DEFINITION_CREATE: 'indicator_definition:create',
    INDICATOR_DEFINITION_UPDATE: 'indicator_definition:update',
    INDICATOR_DEFINITION_DELETE: 'indicator_definition:delete',

    // Expense type
    EXPENSE_TYPE_READ: 'expense_type:read',
    EXPENSE_TYPE_CREATE: 'expense_type:create',
    EXPENSE_TYPE_UPDATE: 'expense_type:update',
    EXPENSE_TYPE_DELETE: 'expense_type:delete',

    // Expense type override
    EXPENSE_TYPE_OVERRIDE_READ: 'expense_type_override:read',
    EXPENSE_TYPE_OVERRIDE_CREATE: 'expense_type_override:create',
    EXPENSE_TYPE_OVERRIDE_UPDATE: 'expense_type_override:update',
    EXPENSE_TYPE_OVERRIDE_DELETE: 'expense_type_override:delete',

    // Billing office
    BILLING_OFFICE_READ: 'billing_office:read',
    BILLING_OFFICE_CREATE: 'billing_office:create',
    BILLING_OFFICE_UPDATE: 'billing_office:update',
    BILLING_OFFICE_DELETE: 'billing_office:delete',

    // Bank files (CUR-2808): the organisation's own bank account, read only. Held by the Accounts role;
    // organisation administrators reach it through `org:admin`, and only they change it.
    ORG_BANK_ACCOUNT_READ: 'org_bank_account:read',

    // Risk category
    RISK_CATEGORY_READ: 'risk_category:read',
    RISK_CATEGORY_CREATE: 'risk_category:create',
    RISK_CATEGORY_UPDATE: 'risk_category:update',
    RISK_CATEGORY_DELETE: 'risk_category:delete',

    // Risk action plan
    RISK_ACTION_PLAN_READ: 'risk_action_plan:read',
    RISK_ACTION_PLAN_CREATE: 'risk_action_plan:create',
    RISK_ACTION_PLAN_UPDATE: 'risk_action_plan:update',
    RISK_ACTION_PLAN_DELETE: 'risk_action_plan:delete',
    RISK_ACTION_PLAN_UPDATE_IS_OWNER: 'risk_action_plan:update:is_owner',
    RISK_ACTION_PLAN_READ_IS_OWNER: 'risk_action_plan:read:is_owner',

    // Questionnaire
    QUESTIONNAIRE_READ: 'questionnaire:read',
    QUESTIONNAIRE_CREATE: 'questionnaire:create',
    QUESTIONNAIRE_UPDATE: 'questionnaire:update',
    QUESTIONNAIRE_DELETE: 'questionnaire:delete',

    // Training period
    TRAINING_PERIOD_READ: 'training_period:read',
    TRAINING_PERIOD_CREATE: 'training_period:create',
    TRAINING_PERIOD_UPDATE: 'training_period:update',
    TRAINING_PERIOD_DELETE: 'training_period:delete',

    // Training record
    TRAINING_RECORD_READ: 'training_record:read',
    TRAINING_RECORD_CREATE: 'training_record:create',
    TRAINING_RECORD_CREATE_OWN: 'training_record:create:own_training',
    TRAINING_RECORD_UPDATE: 'training_record:update',
    TRAINING_RECORD_DELETE: 'training_record:delete',

    // Control
    CONTROL_READ: 'control:read',
    CONTROL_CREATE: 'control:create',
    CONTROL_UPDATE: 'control:update',
    CONTROL_DELETE: 'control:delete',
    CONTROL_READ_OWN_HANDLING_PARTIES: 'control:read:own_handling_parties',
    CONTROL_READ_OWNER_WITHOUT_HANDLING_PARTIES: 'control:read:owner_without_handling_parties',
    CONTROL_UPDATE_OWN_HANDLING_PARTIES: 'control:update:own_handling_parties',
    CONTROL_UPDATE_IS_OWNER: 'control:update:is_owner',

    // Risk
    RISK_READ: 'risk:read',
    RISK_CREATE: 'risk:create',
    RISK_UPDATE: 'risk:update',
    RISK_DELETE: 'risk:delete',
    RISK_READ_OWN_HANDLING_PARTIES: 'risk:read:own_handling_parties',
    RISK_READ_OWNER_WITHOUT_HANDLING_PARTIES: 'risk:read:owner_without_handling_parties',
    RISK_UPDATE_OWN_HANDLING_PARTIES: 'risk:update:own_handling_parties',
    RISK_UPDATE_IS_OWNER: 'risk:update:is_owner',

    // Org
    ORG_UPDATE: 'org:update',

    // FAQ
    FAQ_READ: 'faq:read',

    // System management
    SYSTEM_ADMIN: 'system:admin',

    // Report
    REPORT_READ_CREATED_BY_USER: 'report:read:created_by_user',
    REPORT_DELETE_CREATED_BY_USER: 'report:delete:created_by_user',

    // Payment user
    PAYMENT_USER_READ: 'payment_user:read',
    PAYMENT_USER_CREATE: 'payment_user:create',
    PAYMENT_USER_UPDATE: 'payment_user:update',
    PAYMENT_USER_DELETE: 'payment_user:delete',

    /** MSP = Material Service Provider - third-party providers of significant outsourced services */
    MSP_READ: 'msp:read',
    MSP_UPDATE: 'msp:update',
    MSP_CREATE: 'msp:create',
    MSP_READ_OWN_HANDLING_PARTIES: 'msp:read:own_handling_parties',
    MSP_READ_OWNER_WITHOUT_HANDLING_PARTIES: 'msp:read:owner_without_handling_parties',
    MSP_UPDATE_OWN_HANDLING_PARTIES: 'msp:update:own_handling_parties',
    MSP_UPDATE_IS_OWNER: 'msp:update:is_owner',

    // MSP Governance Check
    MSP_GOVERNANCE_CHECK_UPDATE_IS_OWNER: 'msp_governance_check:update:is_owner',
    MSP_GOVERNANCE_CHECK_DELETE_IS_OWNER: 'msp_governance_check:delete:is_owner',

    // Access to the org config & settings
    SETTINGS_READ: 'settings:read',
    SETTINGS_UPDATE: 'settings:update',

    // Saved table views
    /** Create and manage shared (organisation-wide) saved table views */
    SAVED_VIEW_SHARE: 'saved_view:share',

    // Inbound email archive
    /** Download the original raw inbound email (.eml) retained for audit */
    INBOUND_EMAIL_ARCHIVE_READ: 'inbound_email_archive:read',

    // MCP access management
    /** Manage MCP (Model Context Protocol) access for the organisation and its users */
    MCP_ACCESS_MANAGE: 'mcp_access:manage',

    /** comm_template = Communication Template - reusable email/notification templates */
    COMM_TEMPLATE_READ: 'comm_template:read',
    COMM_TEMPLATE_CREATE: 'comm_template:create',
    COMM_TEMPLATE_UPDATE: 'comm_template:update',
    COMM_TEMPLATE_DELETE: 'comm_template:delete',

    /** comm_signature = Communication Signature - reusable email signatures */
    COMM_SIGNATURE_READ: 'comm_signature:read',
    COMM_SIGNATURE_CREATE: 'comm_signature:create',
    COMM_SIGNATURE_UPDATE: 'comm_signature:update',
    COMM_SIGNATURE_DELETE: 'comm_signature:delete',

    // Conflict of Interest management
    CONFLICT_OF_INTEREST_CREATE: 'conflict_of_interest:create',
    CONFLICT_OF_INTEREST_READ: 'conflict_of_interest:read',
    CONFLICT_OF_INTEREST_UPDATE: 'conflict_of_interest:update',
    CONFLICT_OF_INTEREST_READ_OWN_HANDLING_PARTIES_OR_IS_OWNER:
        'conflict_of_interest:read:own_handling_parties_or_is_owner',
    CONFLICT_OF_INTEREST_UPDATE_IS_OWNER: 'conflict_of_interest:update:is_owner',
    CONFLICT_OF_INTEREST_UPDATE_OWN_HANDLING_PARTIES: 'conflict_of_interest:update:own_handling_parties',

    // AI assistant
    AI_ASSISTANT_USE: 'ai_assistant:use',

    // Document analyzer
    DOCUMENT_ANALYZER_USE: 'document_analyzer:use',

    // Market PDS comparison tool
    MARKET_PDS_ACCESS: 'market_pds:access',

    // Form schema
    FORM_SCHEMA_READ: 'form_schema:read',
    FORM_SCHEMA_CREATE: 'form_schema:create',
    FORM_SCHEMA_UPDATE: 'form_schema:update',
    FORM_SCHEMA_DELETE: 'form_schema:delete',

    // Register (compliance registers — CUR-644)
    REGISTER_READ: 'register:read',
    REGISTER_UPDATE: 'register:update',

    // User attestation
    USER_ATTESTATION_READ: 'user_attestation:read',
    USER_ATTESTATION_CREATE: 'user_attestation:create',
    USER_ATTESTATION_UPDATE: 'user_attestation:update',
    // Website checker
    WEBSITE_CHECKER_ACCESS: 'website_checker:access',

    // Compliance audit
    COMPLIANCE_AUDIT_ACCESS: 'compliance_audit:access',
    COMPLIANCE_AUDIT_MANAGE: 'compliance_audit:manage',

    // Fleet portal (external fleet-manager surface)
    FLEET_PORTAL_ACCESS: 'fleet_portal:access',
    FLEET_PORTAL_READ: 'fleet_portal:read',
    FLEET_AUTHORISATION_APPROVE: 'fleet_authorisation:approve',

    // Admin Console Module Permissions
    // These permissions control access to specific modules within the admin console (orgId = -1)
    /** Create new organisations in admin console */
    ADMIN_ORGS_CREATE: 'admin_orgs:create',
    /** Update existing organisations in admin console */
    ADMIN_ORGS_UPDATE: 'admin_orgs:update',
    /** Full access to user administration in admin console */
    ADMIN_USERS_FULL: 'admin_users:full',
    /** Full access to content and library management (obligations, controls, FAQ) */
    ADMIN_CONTENT_FULL: 'admin_content:full',
    /** Full access to communications and branding (templates, app text) */
    ADMIN_COMMS_FULL: 'admin_comms:full',
    /** Full access to AI configuration and analytics */
    ADMIN_AI_FULL: 'admin_ai:full',
    /** Full access to billing and wallet management */
    ADMIN_BILLING_FULL: 'admin_billing:full',
    /** Full access to system operations (database, jobs, logs, monitoring) */
    ADMIN_SYSTEM_FULL: 'admin_system:full',
    /** Cross-org read-only ops access (user accounts, email envelopes, entity/audit state) for incident debugging via the ops-read MCP */
    ADMIN_OPS_READ_FULL: 'admin_ops_read:full',

    // Admin Console Module Permissions - Read-Only variants
    /** Read-only access to organisation management in admin console */
    ADMIN_ORGS_READ: 'admin_orgs:read',
    /** Read-only access to user administration in admin console */
    ADMIN_USERS_READ: 'admin_users:read',
    /** Read-only access to content and library management (obligations, controls, FAQ) */
    ADMIN_CONTENT_READ: 'admin_content:read',
    /** Read-only access to communications and branding (templates, app text) */
    ADMIN_COMMS_READ: 'admin_comms:read',
    /** Read-only access to AI configuration and analytics */
    ADMIN_AI_READ: 'admin_ai:read',
    /** Access raw (unsanitised) LLM request log content via includeRaw=true; granted to super_admin only */
    ADMIN_AI_RAW_LOGS: 'admin_ai:raw_logs',
    /** Read-only access to billing and wallet management */
    ADMIN_BILLING_READ: 'admin_billing:read',
    /** Read-only access to system operations (database, jobs, logs, monitoring) */
    ADMIN_SYSTEM_READ: 'admin_system:read',

    // Audit trail
    AUDIT_TRAIL_READ: 'audit_trail:read',

    // Support session management (organisation-side)
    /** View support session requests and history for the organisation */
    SUPPORT_SESSION_READ: 'support_session:read',
    /** Approve or reject support session requests for the organisation */
    SUPPORT_SESSION_APPROVE: 'support_session:approve',

    // Support session management (admin console)
    /** Full access to support session management in admin console (create, cancel requests) */
    ADMIN_SUPPORT_FULL: 'admin_support:full',
    /** Read-only access to support session management in admin console */
    ADMIN_SUPPORT_READ: 'admin_support:read',

    // Service API Token management
    /** View service API tokens for the organisation */
    SERVICE_API_TOKEN_READ: 'service_api_token:read',
    /** Create new service API tokens */
    SERVICE_API_TOKEN_CREATE: 'service_api_token:create',
    /** Modify service API token settings (name, scopes, IP allowlist) */
    SERVICE_API_TOKEN_UPDATE: 'service_api_token:update',
    /** Revoke service API tokens */
    SERVICE_API_TOKEN_DELETE: 'service_api_token:delete',

    // Handling party management (Service API)
    /** List handling parties via the Service API */
    HANDLING_PARTY_LIST: 'handling_party:list',
    /** Create handling parties via the Service API */
    HANDLING_PARTY_CREATE: 'handling_party:create',
    /** Update handling parties via the Service API */
    HANDLING_PARTY_UPDATE: 'handling_party:update',

    // Data export (read-only bulk access via Service API)
    /** Export claims in bulk via the Service API */
    CLAIM_EXPORT: 'claim:export',
    /** Export incidents in bulk via the Service API */
    INCIDENT_EXPORT: 'incident:export',
    /** Export complaints in bulk via the Service API */
    COMPLAINT_EXPORT: 'complaint:export',
    /** Export obligations in bulk via the Service API */
    OBLIGATION_EXPORT: 'obligation:export',
    /** Export risks in bulk via the Service API */
    RISK_EXPORT: 'risk:export',
    /** Export handling parties in bulk via the Service API */
    HANDLING_PARTY_EXPORT: 'handling_party:export',
} as const satisfies Record<string, `${string}:${string}`>;

/**
 * Human-readable descriptions for each permission.
 * Used for documentation and UI tooltips.
 */
export const PERMISSION_DESCRIPTIONS: Partial<Record<Permission, string>> = {
    // Super admin
    'super:admin': 'System-wide administrative access across ALL organisations (platform operators only)',

    // Organization Administration
    'org:admin': 'Full administrative access within a SINGLE organisation',

    // User management
    'user:create': 'Create new users in the organisation',
    'user:read': 'View all users in the organisation',
    'user:update': 'Modify user details and settings',

    // Task management
    'task:manage': 'Full access to task management features',
    'task:reassign:selected': 'Reassign selected tasks to other users',

    // Claim management
    'claim:create': 'Create new claims',
    'claim:read': 'View a single claim in the organisation',
    'claim:list': 'List claims in the organisation with pagination',
    'claim:read:unallocated': 'View claims that have no handling parties assigned',
    'claim:read:own_handling_parties':
        "View claims assigned to user's handling parties, plus unallocated claims the user created",
    'claim:update': 'Modify any claim in the organisation',
    'claim:update:unallocated': 'Modify claims that have no handling parties assigned',
    'claim:update:own_handling_parties':
        "Modify claims assigned to user's handling parties, plus unallocated claims the user created",
    'claim:update:is_owner': 'Modify claims where user is the owner',
    'claim:read:all': 'View every claim in the organisation, read only',
    'claim_payment:accounts': 'Give the final approval on payments and mark them paid',

    // Claim Decision Engine
    'claim_decision_engine:manage':
        'Manage decision engine configuration (loss causes, red flags, limits, fast track rules)',
    'claim_decision_engine:use': 'Use decision engine features (run assessments, view results)',

    // Contact management
    'contact:read': 'View contacts (requires claim or supplier access)',
    'contact:create': 'Create new contacts',
    'contact:update': 'Modify contact details',
    'contact:delete': 'Remove contacts',

    // Complaint management
    'complaint:create': 'Create new complaints',
    'complaint:read': 'View all complaints in the organisation',
    'complaint:list': 'List complaints in the organisation with pagination',
    'complaint:read:own_handling_parties_or_is_owner':
        "View complaints assigned to user's handling parties or where user is owner",
    'complaint:update': 'Modify any complaint in the organisation',
    'complaint:update:own_handling_parties': "Modify complaints assigned to user's handling parties",
    'complaint:update:is_owner': 'Modify complaints where user is the owner',

    // Incident management
    'incident:create': 'Create new incidents',
    'incident:read': 'View all incidents in the organisation',
    'incident:read:own_handling_parties': "View incidents assigned to user's handling parties",
    'incident:read:owner_without_handling_parties':
        'View incidents where user is owner and no handling parties assigned',
    'incident:update': 'Modify any incident in the organisation',
    'incident:update:own_handling_parties': "Modify incidents assigned to user's handling parties",
    'incident:update:is_owner': 'Modify incidents where user is the owner',

    // Obligation management
    'obligation:read': 'View all obligations in the organisation',
    'obligation:read:own_handling_parties': "View obligations assigned to user's handling parties",
    'obligation:update': 'Modify any obligation in the organisation',
    'obligation:update:own_handling_parties': "Modify obligations assigned to user's handling parties",
    'obligation:update:is_owner': 'Modify obligations where user is the owner',

    // Message classification (auto-detect tool)
    'message_classification:access': 'Access the auto-detect tool (AI inbox and dashboard)',
    'message_classification:create': 'Create new message classifications',
    'message_classification:read': 'View all message classifications',
    'message_classification:update': 'Modify and review any message classification',
    'message_classification:delete': 'Delete any message classification',

    // Message-classification vulnerability detection
    'message_classification:vulnerability:view': 'View detected vulnerabilities and active contact vulnerability flags',
    'message_classification:vulnerability:manage':
        'Confirm, dismiss, promote, resolve and extend the expiry of vulnerability detections and contact flags',
    'message_classification:vulnerability:view_sensitive':
        'View sensitive vulnerability details (family violence, severe mental health, bereavement) — required to see type and evidence on these rows',

    // Product type
    'product_type:create': 'Create new product types',
    'product_type:update': 'Modify product types',
    'product_type:delete': 'Remove product types',

    // Supplier
    'supplier:read': 'View suppliers',
    'supplier:create': 'Create new suppliers',
    'supplier:update': 'Modify supplier details',
    'supplier:delete': 'Remove suppliers',

    // Financial section
    'financial_section:read': 'View financial sections',
    'financial_section:update': 'Modify financial sections',

    // Transaction type
    'transaction_type:read': 'View transaction types',

    // Cost code
    'cost_code:read': 'View cost codes',

    // PDS
    'pds:read': 'View Product Disclosure Statements',
    'pds:create': 'Create new PDS records',
    'pds:update': 'Modify PDS records',

    // PDS version
    'pds_version:read': 'View PDS versions',
    'pds_version:create': 'Create new PDS versions',
    'pds_version:update': 'Modify PDS versions',

    // Insurer
    'insurer:read': 'View insurers',
    'insurer:create': 'Create new insurers',
    'insurer:update': 'Modify insurer details',
    'insurer:delete': 'Remove insurers',

    // Billing
    'billing:read': 'View billing information and wallet balance',
    'billing:add_funds': 'Add funds to organisation wallet',

    // Indicator definition
    'indicator_definition:read': 'View indicator definitions',
    'indicator_definition:create': 'Create new indicator definitions',
    'indicator_definition:update': 'Modify indicator definitions',
    'indicator_definition:delete': 'Remove indicator definitions',

    // Expense type
    'expense_type:read': 'View expense types',
    'expense_type:create': 'Create new expense types',
    'expense_type:update': 'Modify expense types',
    'expense_type:delete': 'Remove expense types',

    // Expense type override
    'expense_type_override:read': 'View expense type overrides',
    'expense_type_override:create': 'Create new expense type overrides',
    'expense_type_override:update': 'Modify expense type overrides',
    'expense_type_override:delete': 'Remove expense type overrides',

    // Billing office
    'billing_office:read': 'View billing offices',
    'billing_office:create': 'Create new billing offices',
    'billing_office:update': 'Modify billing office details',
    'billing_office:delete': 'Remove billing offices',

    // Bank files
    'org_bank_account:read': "View the organisation's bank account that bank files pay from",

    // Risk category
    'risk_category:read': 'View risk categories',
    'risk_category:create': 'Create new risk categories',
    'risk_category:update': 'Modify risk categories',
    'risk_category:delete': 'Remove risk categories',

    // Risk action plan
    'risk_action_plan:read': 'View all risk action plans',
    'risk_action_plan:create': 'Create new risk action plans',
    'risk_action_plan:update': 'Modify any risk action plan',
    'risk_action_plan:delete': 'Remove risk action plans',
    'risk_action_plan:read:is_owner': 'View risk action plans where user is owner',
    'risk_action_plan:update:is_owner': 'Modify risk action plans where user is owner',

    // Questionnaire
    'questionnaire:read': 'View questionnaires',
    'questionnaire:create': 'Create new questionnaires',
    'questionnaire:update': 'Modify questionnaires',
    'questionnaire:delete': 'Remove questionnaires',

    // Training period
    'training_period:read': 'View training periods',
    'training_period:create': 'Create new training periods',
    'training_period:update': 'Modify training periods',
    'training_period:delete': 'Remove training periods',

    // Training record
    'training_record:read': 'View training records',
    'training_record:create': 'Create new training records',
    'training_record:create:own_training': 'Create training records for own user only',
    'training_record:update': 'Modify training records',
    'training_record:delete': 'Remove training records',

    // Control
    'control:read': 'View all controls in the organisation',
    'control:create': 'Create new controls',
    'control:update': 'Modify any control',
    'control:delete': 'Remove controls',
    'control:read:own_handling_parties': "View controls assigned to user's handling parties",
    'control:read:owner_without_handling_parties': 'View controls where user is owner and no handling parties assigned',
    'control:update:own_handling_parties': "Modify controls assigned to user's handling parties",
    'control:update:is_owner': 'Modify controls where user is the owner',

    // Risk
    'risk:read': 'View all risks in the organisation',
    'risk:create': 'Create new risks',
    'risk:update': 'Modify any risk',
    'risk:delete': 'Remove risks',
    'risk:read:own_handling_parties': "View risks assigned to user's handling parties",
    'risk:read:owner_without_handling_parties': 'View risks where user is owner and no handling parties assigned',
    'risk:update:own_handling_parties': "Modify risks assigned to user's handling parties",
    'risk:update:is_owner': 'Modify risks where user is the owner',

    // Org
    'org:update': 'Modify organisation settings',

    // FAQ
    'faq:read': 'View FAQs',

    // System management
    'system:admin': 'Reserved for system-level operations (background jobs, migrations)',

    // Report
    'report:read:created_by_user': 'View reports created by user',
    'report:delete:created_by_user': 'Delete reports created by user',

    // Payment user
    'payment_user:read': 'View payment users',
    'payment_user:create': 'Create new payment users',
    'payment_user:update': 'Modify payment user details',
    'payment_user:delete': 'Remove payment users',

    // Material service provider
    'msp:read': 'View all material service providers',
    'msp:create': 'Create new material service providers',
    'msp:update': 'Modify any material service provider',
    'msp:read:own_handling_parties': "View MSPs assigned to user's handling parties",
    'msp:read:owner_without_handling_parties': 'View MSPs where user is owner and no handling parties assigned',
    'msp:update:own_handling_parties': "Modify MSPs assigned to user's handling parties",
    'msp:update:is_owner': 'Modify MSPs where user is the owner',

    // MSP Governance Check
    'msp_governance_check:update:is_owner': 'Modify governance checks where user is the owner',
    'msp_governance_check:delete:is_owner': 'Delete governance checks where user is the owner',

    // Settings
    'settings:read': 'View organisation settings and configuration',
    'settings:update': 'Modify organisation settings and configuration',

    // Saved table views
    'saved_view:share': 'Create and manage shared (organisation-wide) saved table views',

    // Inbound email archive
    'inbound_email_archive:read': 'Download the original raw inbound email (.eml) retained for audit',

    // MCP access management
    'mcp_access:manage': 'Manage MCP access for the organisation and its users',

    // Communication template
    'comm_template:read': 'View communication templates',
    'comm_template:create': 'Create new communication templates',
    'comm_template:update': 'Modify communication templates',
    'comm_template:delete': 'Remove communication templates',

    // Communication signature
    'comm_signature:read': 'View communication signatures',
    'comm_signature:create': 'Create new communication signatures',
    'comm_signature:update': 'Modify communication signatures',
    'comm_signature:delete': 'Remove communication signatures',

    // Conflict of Interest
    'conflict_of_interest:create': 'Create new conflict of interest records',
    'conflict_of_interest:read': 'View all conflict of interest records',
    'conflict_of_interest:update': 'Modify any conflict of interest record',
    'conflict_of_interest:read:own_handling_parties_or_is_owner':
        "View conflicts assigned to user's handling parties or where user is owner",
    'conflict_of_interest:update:is_owner': 'Modify conflicts where user is the owner',
    'conflict_of_interest:update:own_handling_parties': "Modify conflicts assigned to user's handling parties",

    // AI assistant
    'ai_assistant:use': 'Use AI assistant features',

    // Document analyzer
    'document_analyzer:use': 'Use document analyser tool',

    // Market PDS comparison tool
    'market_pds:access': 'Access PDS comparison and analysis tool',

    // Form schema
    'form_schema:read': 'View form schemas',
    'form_schema:create': 'Create new form schemas',
    'form_schema:update': 'Modify form schemas',
    'form_schema:delete': 'Remove form schemas',

    // Register
    'register:read': 'View compliance registers and their records',
    'register:update': 'Create and modify compliance registers and their records',

    // User attestation
    'user_attestation:read': 'View user attestations',
    'user_attestation:create': 'Create new user attestations',
    'user_attestation:update': 'Modify user attestations',

    // Website checker
    'website_checker:access': 'Access website checker tool',

    // Compliance audit
    'compliance_audit:access': 'Open the compliance audit tool and view audits, evidence and insights',
    'compliance_audit:manage': 'Create, run, answer, override and annotate compliance audits',

    // Fleet portal
    'fleet_portal:access': 'Access the fleet portal shell and navigation',
    'fleet_portal:read':
        'Read fleet portal data scoped to own client memberships or coordinated portfolio (clients, vehicles, claims, KPIs)',
    'fleet_authorisation:approve':
        'List and decide repair authorisation requests in the fleet portal (approve, decline, query)',

    // Admin Console Module Permissions
    'admin_orgs:create': 'Create new organisations in admin console',
    'admin_orgs:update': 'Update existing organisations in admin console',
    'admin_users:full': 'Full access to user administration in admin console',
    'admin_content:full': 'Full access to content and library management (obligations, controls, FAQ)',
    'admin_comms:full': 'Full access to communications and branding (templates, app text)',
    'admin_ai:full': 'Full access to AI configuration and analytics',
    'admin_ai:raw_logs':
        'Access raw (unsanitised) LLM request log content and AI feedback comments via includeRaw=true; reserved for super_admin',
    'admin_billing:full': 'Full access to billing and wallet management',
    'admin_system:full': 'Full access to system operations (database, jobs, logs, monitoring)',
    'admin_ops_read:full':
        'Cross-org read-only ops access (user accounts, email envelopes, entity and audit state) for incident debugging via the ops-read MCP',

    // Admin Console Module Permissions - Read-Only
    'admin_orgs:read': 'Read-only access to organisation management in admin console',
    'admin_users:read': 'Read-only access to user administration in admin console',
    'admin_content:read': 'Read-only access to content and library management (obligations, controls, FAQ)',
    'admin_comms:read': 'Read-only access to communications and branding (templates, app text)',
    'admin_ai:read': 'Read-only access to AI configuration and analytics',
    'admin_billing:read': 'Read-only access to billing and wallet management',
    'admin_system:read': 'Read-only access to system operations (database, jobs, logs, monitoring)',

    // Audit trail
    'audit_trail:read': 'View audit trail records for the organisation',

    // Support session management (organisation-side)
    'support_session:read': 'View support session requests and history for this organisation',
    'support_session:approve': 'Approve or reject support session requests for this organisation',

    // Support session management (admin console)
    'admin_support:full': 'Full access to support session management in admin console (create, cancel requests)',
    'admin_support:read': 'Read-only access to support session management in admin console',

    // Service API Token management
    'service_api_token:read': 'View service API tokens for this organisation',
    'service_api_token:create': 'Create new service API tokens for external integrations',
    'service_api_token:update': 'Modify service API token settings (name, scopes, IP allowlist)',
    'service_api_token:delete': 'Revoke service API tokens',

    // Handling party management (Service API)
    'handling_party:list': 'List handling parties via the Service API',
    'handling_party:create': 'Create handling parties via the Service API',
    'handling_party:update': 'Update handling parties via the Service API',

    // Data export (Service API)
    'claim:export': 'Export claims in bulk via the Service API',
    'incident:export': 'Export incidents in bulk via the Service API',
    'complaint:export': 'Export complaints in bulk via the Service API',
    'obligation:export': 'Export obligations in bulk via the Service API',
    'risk:export': 'Export risks in bulk via the Service API',
    'handling_party:export': 'Export handling parties in bulk via the Service API',
} as const;

/**
 * Describe which additional permissions are required to access a permission
 * The required permissions are listed in the value of the key and at least one of them is required to access the permission
 */
export const PERMISSION_DEPENDENCIES = {
    // Contact
    'contact:read': {
        atLeastOne: [
            'claim:read',
            'claim:read:all',
            'claim:read:unallocated',
            'claim:read:own_handling_parties',
            'supplier:read',
        ],
    },
    'contact:create': {
        atLeastOne: [
            'claim:update',
            'claim:update:unallocated',
            'claim:update:own_handling_parties',
            'claim:update:is_owner',
            'supplier:update',
        ],
    },
    'contact:update': {
        atLeastOne: [
            'claim:update',
            'claim:update:unallocated',
            'claim:update:own_handling_parties',
            'claim:update:is_owner',
            'supplier:update',
        ],
    },
    'contact:delete': {
        atLeastOne: [
            'claim:update',
            'claim:update:unallocated',
            'claim:update:own_handling_parties',
            'claim:update:is_owner',
            'supplier:update',
        ],
    },
} as const satisfies Partial<Record<Permission, { atLeastOne: readonly Permission[] }>>;

/**
 * List of permissions required to access organization configuration section
 */
export const ORG_CONFIG_PERMISSIONS = ['settings:update', 'user:create', 'user:update'] as const satisfies Permission[];

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
